/**
 * Global Error Handling Utilities
 * Centralized error handling, logging, and reporting system
 */

import { env } from './env';

// ============================================================================
// Error Types
// ============================================================================

export interface ErrorContext {
  filename?: string;
  lineno?: number;
  colno?: number;
  originalError?: unknown;
  [key: string]: unknown;
}

export interface AppError {
  name: string;
  message: string;
  stack?: string | undefined;
  code?: string | number | undefined;
  context?: ErrorContext | undefined;
  timestamp: string;
  url: string;
  userAgent: string;
}

export interface ErrorReport {
  error: AppError;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'network' | 'auth' | 'validation' | 'ui' | 'data' | 'unknown';
  userId?: string;
  sessionId?: string;
  componentStack?: string;
}

// ============================================================================
// Error Logger Class
// ============================================================================

export class ErrorLogger {
  private static instance: ErrorLogger;
  private errorQueue: ErrorReport[] = [];
  private maxQueueSize = 50;

  private constructor() {
    // Set up global error handlers
    this.setupGlobalHandlers();
  }

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  private setupGlobalHandlers(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleUnhandledRejection(event);
    });

    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      this.handleUncaughtError(event);
    });

    // Handle React errors (if not caught by boundaries)
    if (env.isDevelopment) {
      const originalConsoleError = console.error;
      console.error = (...args: any[]) => {
        this.handleConsoleError(args);
        originalConsoleError.apply(console, args);
      };
    }
  }

  private handleUnhandledRejection(event: PromiseRejectionEvent): void {
    const error = this.createErrorFromUnknown(event.reason);
    const report: ErrorReport = {
      error,
      severity: 'high',
      category: 'network',
    };

    this.logError(report);
    this.reportError(report);

    // Prevent default browser error handling in production
    if (!env.isDevelopment) {
      event.preventDefault();
    }
  }

  private handleUncaughtError(event: ErrorEvent): void {
    const error = this.createErrorFromUnknown(event.error || event.message);
    const report: ErrorReport = {
      error: {
        ...error,
        context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      },
      severity: 'critical',
      category: 'ui',
    };

    this.logError(report);
    this.reportError(report);
  }

  private handleConsoleError(args: any[]): void {
    // Only handle React errors and warnings
    if (args.length > 0 && typeof args[0] === 'string') {
      const message = args[0];
      if (message.includes('Warning:') || message.includes('Error:')) {
        const error = this.createErrorFromUnknown(message);
        const report: ErrorReport = {
          error,
          severity: message.includes('Error:') ? 'high' : 'medium',
          category: 'ui',
        };

        this.logError(report);
      }
    }
  }

  private createErrorFromUnknown(error: unknown): AppError {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        ...(error.stack && { stack: error.stack }),
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };
    }

    if (typeof error === 'string') {
      return {
        name: 'UnknownError',
        message: error,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };
    }

    return {
      name: 'UnknownError',
      message: 'An unknown error occurred',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      context: { originalError: error },
    };
  }

  // Public API
  logError(report: ErrorReport): void {
    // Add to queue
    this.errorQueue.push(report);

    // Maintain queue size
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }

    // Console logging based on environment and log level
    if (env.shouldLog('error')) {
      console.error(
        `[${report.severity.toUpperCase()}] ${report.category}:`,
        report.error,
      );
    }
  }

  reportError(report: ErrorReport): void {
    // In a real application, you would send this to a service like Sentry
    if (env.isProduction) {
      this.sendToErrorReportingService(report);
    }
  }

  private async sendToErrorReportingService(
    report: ErrorReport,
  ): Promise<void> {
    try {
      // Example: Send to monitoring service
      const payload = {
        ...report,
        environment: env.nodeEnv,
        appVersion: env.appVersion,
      };

      // In a real app, you would use fetch or axios to send to your error reporting endpoint
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(payload),
      // });

      console.log('📊 Error report sent:', payload);
    } catch (error) {
      console.error('Failed to send error report:', error);
    }
  }

  getErrorQueue(): ErrorReport[] {
    return [...this.errorQueue];
  }

  clearErrorQueue(): void {
    this.errorQueue = [];
  }
}

// ============================================================================
// Global Error Handler Instance
// ============================================================================

export const errorLogger = ErrorLogger.getInstance();

// ============================================================================
// Error Boundary Helper
// ============================================================================

export function createErrorReport(
  error: Error,
  errorInfo?: { componentStack?: string },
  context?: ErrorContext,
): ErrorReport {
  const appError: AppError = {
    name: error.name,
    message: error.message,
    stack: error.stack,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    context,
  };

  // Determine severity and category based on error type
  let severity: ErrorReport['severity'] = 'medium';
  let category: ErrorReport['category'] = 'unknown';

  if (error.name === 'NetworkError' || error.message.includes('fetch')) {
    severity = 'high';
    category = 'network';
  } else if (error.name === 'AuthError' || error.message.includes('auth')) {
    severity = 'high';
    category = 'auth';
  } else if (error.name === 'ValidationError') {
    severity = 'low';
    category = 'validation';
  } else if (
    error.message.includes('render') ||
    error.message.includes('component')
  ) {
    severity = 'high';
    category = 'ui';
  }

  return {
    error: appError,
    severity,
    category,
    ...(errorInfo?.componentStack && {
      componentStack: errorInfo.componentStack,
    }),
  };
}

// ============================================================================
// Error Recovery Utilities
// ============================================================================

export function withErrorRecovery<T extends (...args: any[]) => any>(
  fn: T,
  fallbackValue?: ReturnType<T>,
  errorHandler?: (error: Error) => void,
): T {
  return ((...args: Parameters<T>) => {
    try {
      return fn(...args);
    } catch (error) {
      if (error instanceof Error) {
        const report = createErrorReport(error);
        errorLogger.logError(report);

        if (errorHandler) {
          errorHandler(error);
        }
      }

      return fallbackValue;
    }
  }) as T;
}

// ============================================================================
// Development Error Overlay
// ============================================================================

export function showErrorOverlay(error: Error, componentStack?: string): void {
  if (!env.isDevelopment) return;

  // Create error overlay for development
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    z-index: 9999;
    padding: 2rem;
    font-family: monospace;
    font-size: 14px;
    overflow: auto;
  `;

  const container = document.createElement('div');
  container.style.cssText = 'max-width: 800px; margin: 0 auto;';

  const title = document.createElement('h2');
  title.style.cssText = 'color: #ff6b6b; margin-bottom: 1rem;';
  title.textContent = '🚨 Runtime Error';
  container.appendChild(title);

  const messageBlock = document.createElement('div');
  messageBlock.style.cssText =
    'background: #2d3748; padding: 1rem; border-radius: 4px; margin-bottom: 1rem;';

  const messageStrong = document.createElement('strong');
  messageStrong.textContent = `${error.name}: ${error.message}`;
  messageBlock.appendChild(messageStrong);
  container.appendChild(messageBlock);

  if (componentStack) {
    const stackBlock = document.createElement('div');
    stackBlock.style.cssText =
      'background: #2d3748; padding: 1rem; border-radius: 4px; margin-bottom: 1rem;';

    const stackLabel = document.createElement('strong');
    stackLabel.textContent = 'Component Stack:';
    stackBlock.appendChild(stackLabel);

    const stackPre = document.createElement('pre');
    stackPre.style.cssText = 'margin-top: 0.5rem; white-space: pre-wrap;';
    stackPre.textContent = componentStack;
    stackBlock.appendChild(stackPre);

    container.appendChild(stackBlock);
  }

  const traceBlock = document.createElement('div');
  traceBlock.style.cssText =
    'background: #2d3748; padding: 1rem; border-radius: 4px;';

  const traceLabel = document.createElement('strong');
  traceLabel.textContent = 'Stack Trace:';
  traceBlock.appendChild(traceLabel);

  const tracePre = document.createElement('pre');
  tracePre.style.cssText = 'margin-top: 0.5rem; white-space: pre-wrap;';
  tracePre.textContent = error.stack || 'No stack trace available';
  traceBlock.appendChild(tracePre);

  container.appendChild(traceBlock);

  const dismissButton = document.createElement('button');
  dismissButton.type = 'button';
  dismissButton.style.cssText =
    'margin-top: 1rem; padding: 0.5rem 1rem; background: #ff6b6b; border: none; border-radius: 4px; cursor: pointer;';
  dismissButton.textContent = 'Dismiss';
  dismissButton.addEventListener('click', () => {
    overlay.remove();
  });

  container.appendChild(dismissButton);
  overlay.appendChild(container);

  document.body.appendChild(overlay);
}
